import { useAtom, useAtomValue } from 'jotai';
import React, { Activity, useEffect, useState } from 'react';

import { DrawerEvents, EmbeddedDrawer } from '@crossnokaye/ux-common/ckds';

import { EquipmentBulkEditDrawer } from './equipment-bulk-edit-drawer';
import { EquipmentDetailsDrawer } from './equipment-details-drawer';
import { drawerIsOpenAtom, drawerTypeAtom } from './state/drawer.state';
import { useOpenBulkEdit } from '../equipment-bulk-edit/hooks/use-open-bulk-edit';
import { pushOutOpenAtom } from '../equipment-bulk-edit/state/bulk-edit-params.state';
import { useSelectEquipment } from '../equipment-details/hooks/use-select-equipment';

type EquipmentDrawerLoaderProps = {
  children?: React.ReactNode;
};

export const EquipmentDrawerLoader = ({
  children,
}: EquipmentDrawerLoaderProps): React.ReactNode => {
  const [isOpen, setIsOpen] = useAtom(drawerIsOpenAtom);
  const drawerType = useAtomValue(drawerTypeAtom);
  const [isVisible, setIsVisible] = useState(false);

  // Get cleanup actions for each drawer type
  const { clearEquipmentAction } = useSelectEquipment();
  const isPushOutOpen = useAtomValue(pushOutOpenAtom);
  const { clearBulkEditAction } = useOpenBulkEdit();

  const closeDrawerAction = () => {
    setIsOpen(false);
  };

  const closeBulkEditAction = async () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    }
  }, [isOpen]);

  const handleDrawerClose = (event: (typeof DrawerEvents)[keyof typeof DrawerEvents]) => {
    if (event === DrawerEvents.CLOSE_COMPLETE) {
      setIsVisible(false);
      if (drawerType === 'details') {
        clearEquipmentAction();
      } else if (drawerType === 'bulk-edit') {
        clearBulkEditAction();
      }
    }
  };

  return (
    <>
      <Activity mode={isOpen || isVisible ? 'visible' : 'hidden'}>
        <EmbeddedDrawer
          open={isOpen}
          options={{ closeOnEscape: !isPushOutOpen }}
          onClose={closeDrawerAction}
          onDrawerEvent={handleDrawerClose}
        >
          {drawerType === 'details' && <EquipmentDetailsDrawer onClose={closeDrawerAction} />}
          {drawerType === 'bulk-edit' && <EquipmentBulkEditDrawer onClose={closeBulkEditAction} />}
        </EmbeddedDrawer>
      </Activity>

      {/* Hide page content while drawer is open or finishing close animation (matches drawer Activity). */}
      <Activity mode={isOpen || isVisible ? 'hidden' : 'visible'}>{children}</Activity>
    </>
  );
};
